/* -------------------------------------------------------------------------- */
/* ProportionBar                                                               */
/* -------------------------------------------------------------------------- */

export interface ProportionSegment {
  key: string;
  label: string;
  count: number;
  /** A CSS color, normally a token reference such as `var(--status-success)`. */
  color: string;
  /** Legend only: draw an outlined dot, for an "absent" state. */
  hollow?: boolean;
  /** Legend only, `grid` layout: a short explanation under the label. */
  description?: string;
}

export interface ProportionBarProps {
  segments: readonly ProportionSegment[];
  /** Accessible name for the bar; each segment's count is appended. */
  label: string;
  /** Bar thickness. */
  size?: "sm" | "md";
  className?: string;
}

/**
 * A single horizontal bar split into segments by count, such as workflow steps
 * by coverage or applications by use. Empty segments are skipped, and an
 * all-zero bar renders as an empty track.
 */
export function ProportionBar({
  segments,
  label,
  size = "md",
  className = "",
}: ProportionBarProps) {
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);
  const summary = segments
    .map((segment) => `${segment.label}: ${segment.count}`)
    .join(", ");
  return (
    <div
      role="img"
      aria-label={`${label}. ${summary}`}
      className={`flex w-full overflow-hidden rounded-(--radius-full) bg-(--bg-canvas) ${
        size === "md" ? "h-3.5" : "h-2.5"
      } ${className}`}
    >
      {total > 0 &&
        segments
          .filter((segment) => segment.count > 0)
          .map((segment) => (
            <div
              key={segment.key}
              className="h-full"
              style={{
                width: `${(segment.count / total) * 100}%`,
                backgroundColor: segment.color,
              }}
              title={`${segment.label}: ${segment.count}`}
            />
          ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* ProportionLegend                                                            */
/* -------------------------------------------------------------------------- */

export interface ProportionLegendProps {
  segments: readonly ProportionSegment[];
  /**
   * `inline` wraps count/label pairs on one line; `grid` lays them out in two
   * columns with each segment's description underneath.
   */
  layout?: "inline" | "grid";
  className?: string;
}

function LegendDot({ segment }: { segment: ProportionSegment }) {
  return segment.hollow ? (
    <span
      aria-hidden={true}
      className="size-2 shrink-0 rounded-full border border-(--border-strong)"
    />
  ) : (
    <span
      aria-hidden={true}
      className="size-2 shrink-0 rounded-full"
      style={{ backgroundColor: segment.color }}
    />
  );
}

/** The key for a `ProportionBar`, in the same segment order. */
export function ProportionLegend({
  segments,
  layout = "inline",
  className = "",
}: ProportionLegendProps) {
  if (layout === "grid") {
    return (
      <dl className={`grid grid-cols-2 gap-4 ${className}`}>
        {segments.map((segment) => (
          <div key={segment.key}>
            <dt className="flex items-center gap-1.5">
              <LegendDot segment={segment} />
              <span className="text-[15px] font-semibold tabular-nums text-(--fg-primary)">
                {segment.count}
              </span>
              <span className="text-[12.5px] font-medium text-(--fg-secondary)">
                {segment.label}
              </span>
            </dt>
            {segment.description && (
              <dd className="mt-0.5 text-[11.5px] leading-snug text-(--fg-muted)">
                {segment.description}
              </dd>
            )}
          </div>
        ))}
      </dl>
    );
  }
  return (
    <ul className={`flex flex-wrap gap-x-6 gap-y-2 ${className}`}>
      {segments.map((segment) => (
        <li key={segment.key} className="flex items-center gap-1.5">
          <LegendDot segment={segment} />
          <span className="text-[12.5px] font-semibold tabular-nums text-(--fg-primary)">
            {segment.count}
          </span>
          <span className="text-[12px] text-(--fg-secondary)">
            {segment.label}
          </span>
        </li>
      ))}
    </ul>
  );
}
